import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Info } from "lucide-react";
import { cn } from "../lib/utils";

type ToastType = "success" | "error" | "info";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastProvider");
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, type: ToastType = "info") => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              "flex items-center gap-3 px-4 py-3 shadow-lg border min-w-[300px] pointer-events-auto transform transition-all duration-300 animate-in slide-in-from-right-5 fade-in font-mono text-xs uppercase tracking-wide",
              t.type === "success" && "bg-bg-surface border-success/30 text-text-primary",
              t.type === "error" && "bg-bg-surface border-error/30 text-text-primary",
              t.type === "info" && "bg-bg-surface border-border-default text-text-primary"
            )}
          >
            {t.type === "success" && <CheckCircle className="w-5 h-5 text-success" />}
            {t.type === "error" && <AlertCircle className="w-5 h-5 text-error" />}
            {t.type === "info" && <Info className="w-5 h-5 text-text-secondary" />}
            
            <p className="text-sm font-medium flex-1">{t.message}</p>
            
            <button 
              onClick={() => removeToast(t.id)}
               className="p-1 hover:bg-bg-panel rounded-none transition-colors"
            >
              <X className="w-4 h-4 text-text-muted" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
